import React, { useState, useEffect, useCallback } from 'react';
import { Settings, CheckCircle2, XCircle, Clock, Mail, Users, Filter, Loader2, Shield } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from '../../hooks/use-toast';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  email_pending: { label: 'Email en attente', color: 'bg-blue-100 text-blue-800', icon: Mail },
  pending_approval: { label: 'En attente approbation', color: 'bg-amber-100 text-amber-800', icon: Clock },
  approved: { label: 'Approuve', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  rejected: { label: 'Refuse', color: 'bg-red-100 text-red-800', icon: XCircle }
};

const ROLE_OPTIONS = [
  { value: 'auditeur', label: 'Auditeur (lecture seule)' },
  { value: 'operateur', label: 'Operateur Logistique' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'field_ops', label: 'Agent Terrain' },
  { value: 'comptable', label: 'Comptable' },
  { value: 'admin', label: 'Administrateur' }
];

const AccessRequestsTab = ({ currentUserRole }) => {
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState([]);
  const [approveDialog, setApproveDialog] = useState({ open: false, request: null, bulk: false });
  const [rejectDialog, setRejectDialog] = useState({ open: false, request: null, bulk: false });
  const [selectedRole, setSelectedRole] = useState('auditeur');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [configDialog, setConfigDialog] = useState(false);
  const [oauthConfig, setOauthConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [oauthStatus, setOauthStatus] = useState(null);
  const [ssoExceptions, setSsoExceptions] = useState([]);
  const [newExceptionEmail, setNewExceptionEmail] = useState('');

  const headers = { Authorization: 'Bearer cookie-auth' };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [reqRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/access-requests`, {
          params: { status_filter: statusFilter, page_size: 100 },
          headers
        }),
        axios.get(`${API_BASE_URL}/api/admin/access-requests/stats`, { headers })
      ]);
      setRequests(reqRes.data.requests || []);
      setStats(statsRes.data || {});
    } catch (err) {
      console.error('Error fetching access requests:', err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      if (approveDialog.bulk) {
        await axios.post(`${API_BASE_URL}/api/admin/access-requests/bulk/approve`, {
          ids: selectedIds,
          role: selectedRole
        }, { headers });
        toast({ title: 'Demandes approuvees', description: `${selectedIds.length} demande(s) approuvee(s) avec le role ${selectedRole}` });
      } else {
        await axios.put(`${API_BASE_URL}/api/admin/access-requests/${approveDialog.request.id}/approve`, {
          role: selectedRole
        }, { headers });
        toast({ title: 'Demande approuvée', description: `Compte cree avec le role ${selectedRole}` });
      }
      setApproveDialog({ open: false, request: null, bulk: false });
      setSelectedIds([]);
      setSelectedRole('auditeur');
      fetchData();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Erreur lors de l\'approbation', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      if (rejectDialog.bulk) {
        await axios.post(`${API_BASE_URL}/api/admin/access-requests/bulk/reject`, {
          ids: selectedIds,
          reason: rejectReason || null
        }, { headers });
        toast({ title: 'Demandes refusees', description: `${selectedIds.length} demande(s) refusee(s)` });
      } else {
        await axios.put(`${API_BASE_URL}/api/admin/access-requests/${rejectDialog.request.id}/reject`, {
          reason: rejectReason || null
        }, { headers });
        toast({ title: 'Demande refusée' });
      }
      setRejectDialog({ open: false, request: null, bulk: false });
      setSelectedIds([]);
      setRejectReason('');
      fetchData();
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Erreur lors du refus', variant: 'destructive' });
    } finally {
      setActionLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const pendingIds = requests.filter(r => r.status === 'pending_approval').map(r => r.id);
    if (selectedIds.length === pendingIds.length && pendingIds.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(pendingIds);
    }
  };

  const fetchConfig = async () => {
    try {
      setConfigLoading(true);
      const [configRes, statusRes, excRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/oauth-settings`, { headers }),
        axios.get(`${API_BASE_URL}/api/admin/oauth-status`, { headers }),
        axios.get(`${API_BASE_URL}/api/admin/staff-sso/exceptions`, { headers })
      ]);
      setOauthConfig(configRes.data);
      setOauthStatus(statusRes.data);
      setSsoExceptions(excRes.data?.exceptions || []);
    } catch (err) {
      toast({ title: 'Erreur', description: 'Impossible de charger la configuration', variant: 'destructive' });
    } finally {
      setConfigLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      setConfigLoading(true);
      await axios.put(`${API_BASE_URL}/api/admin/oauth-settings`, oauthConfig, { headers });
      toast({ title: 'Configuration sauvegardee' });
      setConfigDialog(false);
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Erreur de sauvegarde', variant: 'destructive' });
    } finally {
      setConfigLoading(false);
    }
  };

  const setSsoException = async (email, allowed) => {
    try {
      const res = await axios.put(`${API_BASE_URL}/api/admin/staff-sso/exception`, { email, allowed }, { headers });
      toast({ title: 'Exception mise a jour', description: res.data?.message });
      setNewExceptionEmail('');
      const excRes = await axios.get(`${API_BASE_URL}/api/admin/staff-sso/exceptions`, { headers });
      setSsoExceptions(excRes.data?.exceptions || []);
    } catch (err) {
      toast({ title: 'Erreur', description: err.response?.data?.detail || 'Operation impossible', variant: 'destructive' });
    }
  };

  const addDomain = () => {
    const d = newDomain.trim().toLowerCase();
    if (!d || d.includes('@') || d.includes(' ') || !d.includes('.')) {
      toast({ title: 'Format invalide', description: 'Utilisez le format exemple.com', variant: 'destructive' });
      return;
    }
    if (oauthConfig.allowed_domains?.includes(d)) {
      toast({ title: 'Domaine déjà présent', variant: 'destructive' });
      return;
    }
    setOauthConfig(prev => ({ ...prev, allowed_domains: [...(prev.allowed_domains || []), d] }));
    setNewDomain('');
  };

  const removeDomain = (domain) => {
    const remaining = (oauthConfig.allowed_domains || []).filter(d => d !== domain);
    if (remaining.length === 0 && oauthConfig.microsoft_enabled) {
      toast({ title: 'Impossible', description: 'Au moins un domaine requis si Microsoft est actif', variant: 'destructive' });
      return;
    }
    setOauthConfig(prev => ({ ...prev, allowed_domains: remaining }));
  };

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const pendingCount = requests.filter(r => r.status === 'pending_approval').length;
  const isAdmin = currentUserRole === 'admin';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold" data-testid="access-requests-title">Demandes d'acces</h3>
          {stats.pending_approval > 0 && (
            <Badge variant="destructive" data-testid="pending-badge">{stats.pending_approval} en attente</Badge>
          )}
        </div>
        {isAdmin && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => { fetchConfig(); setConfigDialog(true); }}
            data-testid="oauth-config-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Configuration OAuth
          </Button>
        )}
      </div>

      {/* Status filter pills */}
      <div className="flex flex-wrap gap-2" data-testid="status-filters">
        {[
          { key: 'all', label: 'Toutes', count: stats.total },
          { key: 'email_pending', label: 'Email en attente', count: stats.email_pending },
          { key: 'pending_approval', label: 'En attente approbation', count: stats.pending_approval },
          { key: 'approved', label: 'Approuvees', count: stats.approved },
          { key: 'rejected', label: 'Refusees', count: stats.rejected }
        ].map(f => (
          <Button
            key={f.key}
            variant={statusFilter === f.key ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(f.key)}
            className="text-xs"
          >
            {f.label} {f.count > 0 ? `(${f.count})` : ''}
          </Button>
        ))}
      </div>

      {/* Bulk actions bar */}
      {selectedIds.length > 0 && (
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border" data-testid="bulk-actions-bar">
          <span className="text-sm font-medium">{selectedIds.length} selectionne(s)</span>
          <Button size="sm" variant="default" className="bg-green-600 hover:bg-green-700"
            onClick={() => setApproveDialog({ open: true, request: null, bulk: true })}
          >
            <CheckCircle2 className="w-4 h-4 mr-1" /> Approuver la selection
          </Button>
          <Button size="sm" variant="destructive"
            onClick={() => setRejectDialog({ open: true, request: null, bulk: true })}
          >
            <XCircle className="w-4 h-4 mr-1" /> Refuser la selection
          </Button>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : requests.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-gray-500">
            <Users className="w-12 h-12 mb-3 text-gray-300" />
            <p className="font-medium">Aucune demande d'acces</p>
            <p className="text-sm">Les demandes via Microsoft apparaitront ici</p>
          </CardContent>
        </Card>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-sm" data-testid="access-requests-table">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="p-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={selectedIds.length > 0 && selectedIds.length === requests.filter(r => r.status === 'pending_approval').length}
                    onChange={toggleSelectAll}
                    className="rounded"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="p-3 text-left">Nom</th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Domaine</th>
                <th className="p-3 text-left">Date</th>
                <th className="p-3 text-left">Email vérifié</th>
                <th className="p-3 text-left">Statut</th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {requests.map(req => {
                const sc = STATUS_CONFIG[req.status] || STATUS_CONFIG.email_pending;
                const StatusIcon = sc.icon;
                return (
                  <tr key={req.id} className="hover:bg-gray-50" data-testid={`request-row-${req.id}`}>
                    <td className="p-3">
                      {req.status === 'pending_approval' && (
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(req.id)}
                          onChange={() => toggleSelect(req.id)}
                          className="rounded"
                        />
                      )}
                    </td>
                    <td className="p-3 font-medium">{req.firstName} {req.lastName}</td>
                    <td className="p-3 text-gray-600">{req.email}</td>
                    <td className="p-3 text-gray-500">{req.domain}</td>
                    <td className="p-3 text-gray-500 whitespace-nowrap">{formatDate(req.requestedAt)}</td>
                    <td className="p-3">
                      {req.emailVerifiedAt ? (
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      ) : (
                        <Clock className="w-4 h-4 text-gray-400" />
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${sc.color}`}>
                        <StatusIcon className="w-3 h-3" />
                        {sc.label}
                      </span>
                      {req.status === 'approved' && req.assignedRole && (
                        <span className="ml-1 text-xs text-gray-500">({req.assignedRole})</span>
                      )}
                    </td>
                    <td className="p-3">
                      {req.status === 'pending_approval' && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="text-green-600 hover:text-green-700 hover:bg-green-50 h-8 px-2"
                            onClick={() => setApproveDialog({ open: true, request: req, bulk: false })}
                            data-testid={`approve-btn-${req.id}`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                            onClick={() => setRejectDialog({ open: true, request: req, bulk: false })}
                            data-testid={`reject-btn-${req.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Approve Dialog */}
      <Dialog open={approveDialog.open} onOpenChange={(open) => { if (!open) setApproveDialog({ open: false, request: null, bulk: false }); }}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approuver {approveDialog.bulk ? `${selectedIds.length} demande(s)` : 'la demande d\'acces'}</DialogTitle>
            <DialogDescription>
              {approveDialog.request && !approveDialog.bulk && (
                <span>{approveDialog.request.firstName} {approveDialog.request.lastName} ({approveDialog.request.email})</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Role a attribuer</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveDialog({ open: false, request: null, bulk: false })}>Annuler</Button>
            <Button className="bg-green-600 hover:bg-green-700" onClick={handleApprove} disabled={actionLoading} data-testid="confirm-approve-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Approuver et creer le compte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog.open} onOpenChange={(open) => { if (!open) setRejectDialog({ open: false, request: null, bulk: false }); }}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Refuser {rejectDialog.bulk ? `${selectedIds.length} demande(s)` : 'la demande d\'acces'}</DialogTitle>
            <DialogDescription>
              {rejectDialog.request && !rejectDialog.bulk && (
                <span>{rejectDialog.request.firstName} {rejectDialog.request.lastName} ({rejectDialog.request.email})</span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Motif du refus (optionnel)</Label>
              <textarea
                className="w-full rounded-md border p-3 text-sm min-h-[80px] resize-none"
                placeholder="Saisissez un motif..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                data-testid="reject-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog({ open: false, request: null, bulk: false })}>Annuler</Button>
            <Button variant="destructive" onClick={handleReject} disabled={actionLoading} data-testid="confirm-reject-btn">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
              Refuser la demande
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* OAuth Config Dialog */}
      <Dialog open={configDialog} onOpenChange={setConfigDialog}>
        <DialogContent className="max-w-lg" data-testid="oauth-config-dialog">
          <DialogHeader>
            <DialogTitle>Configuration OAuth</DialogTitle>
            <DialogDescription>Gerez les parametres d'authentification Microsoft</DialogDescription>
          </DialogHeader>
          {configLoading && !oauthConfig ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : oauthConfig && (
            <div className="space-y-5 py-2">
              {/* Connection Status */}
              {oauthStatus && (
                <div className={`rounded-lg border p-4 ${oauthStatus.microsoft?.configured ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`} data-testid="oauth-status-block">
                  <div className="flex items-center gap-2 mb-1">
                    {oauthStatus.microsoft?.configured ? (
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    ) : (
                      <Shield className="w-5 h-5 text-amber-600" />
                    )}
                    <span className="font-medium text-sm">
                      {oauthStatus.microsoft?.configured ? 'Microsoft Azure AD : Connecte' : 'Microsoft Azure AD : Non configure'}
                    </span>
                  </div>
                  {oauthStatus.microsoft?.configured ? (
                    <p className="text-xs text-green-700 ml-7">
                      Tenant : {oauthStatus.microsoft.tenant_id} | Client ID : {oauthStatus.microsoft.client_id_preview}
                    </p>
                  ) : (
                    <div className="ml-7">
                      <p className="text-xs text-amber-700">Les credentials doivent etre configures cote serveur par votre equipe IT.</p>
                      <a
                        href="https://portal.azure.com/#blade/Microsoft_AAD_RegisteredApps/ApplicationsListBlade"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
                      >
                        Voir le guide de configuration Azure AD
                      </a>
                    </div>
                  )}
                </div>
              )}
              {/* Microsoft toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-medium">Microsoft</Label>
                  <p className="text-xs text-gray-500">Activer l'authentification Microsoft</p>
                </div>
                <Switch
                  checked={oauthConfig.microsoft_enabled}
                  onCheckedChange={(v) => setOauthConfig(prev => ({ ...prev, microsoft_enabled: v }))}
                  data-testid="microsoft-toggle"
                />
              </div>

              {/* Domains */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Domaines autorises</Label>
                <div className="space-y-1">
                  {(oauthConfig.allowed_domains || []).map(d => (
                    <div key={d} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-sm">
                      <span>{d}</span>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-red-500 hover:text-red-700"
                        onClick={() => removeDomain(d)}
                      >
                        Supprimer
                      </Button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    placeholder="nouveau-domaine.com"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    className="text-sm"
                    data-testid="new-domain-input"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDomain())}
                  />
                  <Button variant="outline" size="sm" onClick={addDomain} data-testid="add-domain-btn">Ajouter</Button>
                </div>
              </div>

              {/* Default role */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Role par defaut</Label>
                <Select
                  value={oauthConfig.default_role || 'auditeur'}
                  onValueChange={(v) => setOauthConfig(prev => ({ ...prev, default_role: v }))}
                >
                  <SelectTrigger data-testid="default-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.filter(r => r.value !== 'admin').map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Security toggles */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">Sécurité</Label>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Exiger la vérification email</span>
                  <Switch
                    checked={oauthConfig.require_email_verification}
                    onCheckedChange={(v) => setOauthConfig(prev => ({ ...prev, require_email_verification: v }))}
                    data-testid="require-email-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Exiger l'approbation admin</span>
                  <Switch
                    checked={oauthConfig.require_admin_approval}
                    onCheckedChange={(v) => setOauthConfig(prev => ({ ...prev, require_admin_approval: v }))}
                    data-testid="require-approval-toggle"
                  />
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <Label className="text-sm font-medium">Forcer Microsoft pour le personnel</Label>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Le staff ne pourra plus se connecter par mot de passe. Les administrateurs gardent
                        leur compte local (accès de secours si Azure est indisponible).
                      </p>
                    </div>
                    <Switch
                      checked={!!oauthConfig.staff_sso_enforced}
                      onCheckedChange={(v) => setOauthConfig(prev => ({ ...prev, staff_sso_enforced: v }))}
                      data-testid="staff-sso-enforced-toggle"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-gray-700">
                      Exceptions : comptes autorises a garder le mot de passe local
                    </Label>
                    {ssoExceptions.length === 0 ? (
                      <p className="text-xs text-gray-500" data-testid="sso-exceptions-empty">Aucune exception</p>
                    ) : (
                      <div className="space-y-1" data-testid="sso-exceptions-list">
                        {ssoExceptions.map(u => (
                          <div key={u.email} className="flex items-center justify-between bg-white rounded px-3 py-2 text-xs border">
                            <span>{u.email} <span className="text-gray-400">({u.role})</span></span>
                            <Button variant="ghost" size="sm" className="h-6 px-2 text-red-500 hover:text-red-700"
                              onClick={() => setSsoException(u.email, false)}
                              data-testid={`remove-sso-exception-${u.email}`}
                            >
                              Retirer
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        placeholder="email@baobab.ci"
                        value={newExceptionEmail}
                        onChange={(e) => setNewExceptionEmail(e.target.value)}
                        className="text-sm"
                        data-testid="new-sso-exception-input"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), newExceptionEmail.trim() && setSsoException(newExceptionEmail.trim().toLowerCase(), true))}
                      />
                      <Button variant="outline" size="sm"
                        onClick={() => newExceptionEmail.trim() && setSsoException(newExceptionEmail.trim().toLowerCase(), true)}
                        data-testid="add-sso-exception-btn"
                      >
                        Ajouter
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialog(false)}>Annuler</Button>
            <Button onClick={saveConfig} disabled={configLoading} data-testid="save-config-btn">
              {configLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccessRequestsTab;
